"use client";

import { useCallback, useEffect, useRef, type FC } from "react";
import { PractitionerCard } from "@/components/practitioner-card";
import { cn } from "@/lib/cn";
import { LOAD_MORE_MARGIN } from "../../lib/map-options";
import type { PinnedPractitioner } from "./practitioner-markers";

type MapCardCarouselProps = {
  pinned: PinnedPractitioner[];
  active: PinnedPractitioner | null;
  expandedId: string | null;
  onSelect: (pin: PinnedPractitioner) => void;
  onToggleExpand: (pin: PinnedPractitioner) => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
};

const SETTLE_MS = 150;

export const MapCardCarousel: FC<MapCardCarouselProps> = ({
  pinned,
  active,
  expandedId,
  onSelect,
  onToggleExpand,
  hasMore,
  isLoadingMore,
  loadMore,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef(new Map<string, HTMLDivElement>());
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /* Marker-click scrolls must not feed back into a selection */
  const programmaticTargetRef = useRef<number | null>(null);

  const setItemRef = useCallback((item: HTMLDivElement | null, id: string) => {
    if (item) itemsRef.current.set(id, item);
    else itemsRef.current.delete(id);
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const item = active ? itemsRef.current.get(active.id) : undefined;
    if (!scroller || !item) return;

    const centered = item.offsetLeft - (scroller.clientWidth - item.clientWidth) / 2;
    /* Clamp like the browser will, so edge cards no-op here and the arrival check below holds */
    const target = Math.max(0, Math.min(centered, scroller.scrollWidth - scroller.clientWidth));
    if (Math.abs(scroller.scrollLeft - target) < 2) return;

    programmaticTargetRef.current = target;
    scroller.scrollTo({ left: target, behavior: "smooth" });
  }, [active]);

  const onScroll = () => {
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);

    settleTimerRef.current = setTimeout(() => {
      const scroller = scrollerRef.current;
      if (!scroller) return;

      /* Settling at the target is the marker-click scroll arriving; elsewhere the user took over */
      const programmaticTarget = programmaticTargetRef.current;
      programmaticTargetRef.current = null;
      if (programmaticTarget !== null && Math.abs(scroller.scrollLeft - programmaticTarget) < 2) return;

      const center = scroller.scrollLeft + scroller.clientWidth / 2;
      let closest: PinnedPractitioner | null = null;
      let best = Infinity;

      for (const pin of pinned) {
        const item = itemsRef.current.get(pin.id);
        if (!item) continue;

        const distance = Math.abs(item.offsetLeft + item.clientWidth / 2 - center);
        if (distance < best) {
          best = distance;
          closest = pin;
        }
      }

      if (closest && closest.id !== active?.id) onSelect(closest);
    }, SETTLE_MS);
  };

  useEffect(
    () => () => {
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const scroller = scrollerRef.current;
    if (!sentinel || !scroller || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { root: scroller, rootMargin: `0px ${LOAD_MORE_MARGIN} 0px 0px` },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, loadMore, pinned.length]);

  if (pinned.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 lg:hidden">
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className={cn(
          /* Pass-through so the scroller's box doesn't swallow taps on map pins; cards re-enable pointer events */
          "pointer-events-none flex snap-x snap-mandatory items-end gap-12 overflow-x-auto px-16 pt-8 pb-16",
          "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        )}
      >
        {pinned.map((pin) => {
          const isActive = active?.id === pin.id;

          return (
            <div
              key={pin.id}
              ref={(item) => setItemRef(item, pin.id)}
              role="button"
              tabIndex={0}
              onClick={() => (isActive ? onToggleExpand(pin) : onSelect(pin))}
              onKeyDown={(event) => {
                /* preventDefault on a bubbled keydown would cancel the nested Contact/See Profile controls */
                if (event.target !== event.currentTarget) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  if (isActive) onToggleExpand(pin);
                  else onSelect(pin);
                }
              }}
              className="pointer-events-auto w-[min(301px,calc(100vw_-_64px))] shrink-0 cursor-pointer snap-center"
            >
              <PractitionerCard
                source={pin.practitioner}
                variant="map-carousel"
                expanded={expandedId === pin.id}
                className={cn("shadow-[0px_2px_7px_1px_rgba(0,0,0,0.15)]", isActive && "border-brand-blue")}
              />
            </div>
          );
        })}

        {hasMore && <div ref={sentinelRef} aria-hidden className="size-1 shrink-0" />}
      </div>
    </div>
  );
};
